import moment from 'moment/moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { AbsenceType } from '~/data/absences/types';
import { AbsenceForm } from '~/features/AbsenceChanges/EditAbsenceDialog/data';
import { EditAbsenceDialogFormProps } from '~/features/AbsenceChanges/EditAbsenceDialog/useEditAbsenceDialogData';
import {
  AbsenceReason,
  YearlyVacationYearOption,
} from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/components/AddAbsenceDialogContent/data';
import { BalancePoolCategory } from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/useVacationBalances';

type PropsType = {
  formProps: EditAbsenceDialogFormProps;
  yearOptions: YearlyVacationYearOption[];
};

export const useAbsenceValidationSchema = (props: PropsType) => {
  const {
    formProps: {
      selectedAbsenceType,

      overlappingAbsence: { hasAbsenceOverlap },
      sickLeaves: {
        isSickLeave,
        isSickLeaveCountSpent,
        getSickLeaveTypeDayLimit,
        getNextSickLeaveCountResetDate,
      },
      balances: {
        getAvailableBalance,
        availableBalances,
        isBalancePoolReasonMatch,
      },
      reasonOptions,
      isHoursFieldEnabled,
    },
    yearOptions,
  } = props;

  const { t } = useTranslation();

  const validationSchema = useMemo(() => {
    const isShortAbsence = selectedAbsenceType === AbsenceType.SHORT;

    const yupTestHasAbsenceOverlap = (
      startDate: Date | null | undefined,
      endDate: Date | null | undefined,
      reasonCode: string | undefined,
    ) => {
      return (
        !!reasonCode &&
        hasAbsenceOverlap(startDate ?? null, endDate ?? null, reasonCode)
      );
    };

    return yup
      .object()
      .shape<AbsenceForm>({
        absenceReason: yup
          .object<AbsenceReason>()
          .nullable(true)
          .oneOf(reasonOptions, t('fieldIsRequired'))
          .required(t('fieldIsRequired')),
        year: yup
          .object<YearlyVacationYearOption>()
          .nullable(true)
          .when(['absenceReason'], (absenceReason, schema) => {
            const isYearlyVacationReason =
              !!availableBalances.balancePools.find(
                (p) =>
                  isBalancePoolReasonMatch(p, absenceReason?.code) &&
                  p.category === BalancePoolCategory.YearlyVacationBalance,
              );

            return isYearlyVacationReason
              ? schema
                  .oneOf(yearOptions, t('fieldIsRequired'))
                  .required(t('fieldIsRequired'))
              : schema;
          }),
        startDate: yup
          .date()
          .required(t('fieldIsRequired'))
          .test(
            'absenceOverlap',
            t('absencesAreOverlapping'),
            (startDate, context) => {
              const { endDate, absenceReason } = context.parent;

              return !yupTestHasAbsenceOverlap(
                startDate,
                endDate,
                absenceReason?.code,
              );
            },
          ),
        endDate: (isShortAbsence
          ? yup.date().required(t('fieldIsRequired'))
          : yup.date().nullable()
        )
          .defined()
          .test(
            'absenceOverlap',
            t('absencesAreOverlapping'),
            (endDate, context) => {
              const { startDate, absenceReason } = context.parent;

              return !yupTestHasAbsenceOverlap(
                startDate,
                endDate,
                absenceReason?.code,
              );
            },
          ),
        days: yup
          .number()
          .when(
            ['startDate', 'endDate', 'hours'],
            (startDate, endDate, hours, schema) => {
              if (
                !isShortAbsence ||
                (isHoursFieldEnabled(startDate, endDate) && hours > 0)
              ) {
                return schema;
              } else {
                return schema.min(1, t('pleaseSelectDates'));
              }
            },
          )
          .when(['absenceReason', 'year'], (absenceReason, year, schema) => {
            if (
              availableBalances.balancePoolConfigs.negativeHolidayBalanceEnabled
            ) {
              return schema;
            }

            const balance = getAvailableBalance(absenceReason, year);
            const availableBalance: number = balance?.balance || 0;

            return !!balance
              ? schema.max(
                  availableBalance,
                  `${t('availableBalanceExceeded')}. \n${t(
                    'availableBalance',
                  )}: ${t('NDays', { days: availableBalance })}`,
                )
              : schema;
          })
          .when(['absenceReason'], (absenceReason, schema) => {
            const reasonCode: string | undefined = absenceReason?.code;

            if (!isShortAbsence || !reasonCode || !isSickLeave(reasonCode)) {
              return schema;
            }

            const limit = getSickLeaveTypeDayLimit(reasonCode);

            return limit > 0
              ? schema.max(
                  limit,
                  t('sickLeaveTypeDaysLimitExceeded', {
                    type: absenceReason?.name ?? '',
                    limit,
                  }),
                )
              : schema;
          }),
        hours: yup.number(),
        plannedWorkingHours: yup.number(),
        info: yup.string(),
        attachment: yup.object<File>().nullable(),
        attachmentId: yup.number(),
        attachmentName: yup.string(),
      })
      .test(
        'sickLeaveCountLimit',
        'sickLeaveCountLimit',
        (formData, context) => {
          const reasonCode = formData?.absenceReason?.code;
          const startDate = formData?.startDate;

          if (
            isShortAbsence &&
            !!reasonCode &&
            isSickLeave(reasonCode) &&
            !!startDate
          ) {
            const countLimitErrorMessage = t('sickLeaveCountLimitExceeded', {
              reset: moment(getNextSickLeaveCountResetDate(startDate)).format(
                'L',
              ),
            });

            if (isSickLeaveCountSpent(reasonCode, startDate)) {
              return context.createError({
                path: 'absenceReason',
                message: countLimitErrorMessage,
              });
            }
          }

          return true;
        },
      );
  }, [
    selectedAbsenceType,
    reasonOptions,
    yearOptions,
    availableBalances,
    getAvailableBalance,
    t,
    isBalancePoolReasonMatch,
    isHoursFieldEnabled,
    hasAbsenceOverlap,
    getSickLeaveTypeDayLimit,
    isSickLeave,
    isSickLeaveCountSpent,
    getNextSickLeaveCountResetDate,
  ]);

  return {
    validationSchema,
  };
};
